import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Row,
  Tag,
  message,
  Typography,
  Popconfirm,
  List,
  Result,
  Spin,
  Alert,
  Modal,
} from 'antd';
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  RedoOutlined,
  ExclamationCircleOutlined,
  ArrowLeftOutlined,
  SyncOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../component/AuthContext';
import { PERMISSIONS } from '../../component/permissions';
import Loading from '../../component/Loading';

const { Title, Paragraph, Text } = Typography;

interface Container {
  name: string;
  status: string;
}

interface SystemDetails {
  id: number;
  name: string;
  description: string;
  status: string;
  health: string;
  uptime: string;
  containers: Container[];
}

interface ApiError {
  status: number;
  message: string;
}

const SystemViewPage: React.FC = () => {
  const { canRead, canWrite } = useAuth();
  const { systemId } = useParams<{ systemId: string }>();
  const navigate = useNavigate();

  // System state
  const [system, setSystem] = useState<SystemDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  // Action‐in‐progress state
  // This tracks which action ('start', 'stop', 'restart') is running
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Logs state
  const [selectedContainer, setSelectedContainer] = useState<string | null>(null);
  const [logs, setLogs] = useState<{ ts: string; line: string }[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [errorLogs, setErrorLogs] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch system details
  const fetchSystemDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/systems/${systemId}`,
        { credentials: 'include' }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw {
          status: res.status,
          message: err.detail || `Error ${res.status}`,
        };
      }
      setSystem(await res.json());
    } catch (err: any) {
      setError({
        status: err.status || 0,
        message:
          err.message ||
          'Could not connect to the server. Please check your connection.',
      });
      setSystem(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSystemDetails();
    // clear logs when switching systems
    setSelectedContainer(null);
    setLogs([]);
    setIsModalOpen(false);
  }, [systemId]);

  // Control actions: start, stop, restart
  const handleControlClick = async (action: 'start' | 'stop' | 'restart') => {
    // Prevent double‑click while another action is in flight
    if (actionLoading) {
      message.warning(`Another operation (${actionLoading}) is in progress.`);
      return;
    }

    setActionLoading(action);
    const key = 'controlAction';
    message.loading({ content: `Sending ${action}…`, key });
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/systems/${systemId}/${action}`,
        { method: 'POST', credentials: 'include' }
      );
      const data = await res.json();

      if (data.status === 'success') {
        message.success({ content: data.message, key, duration: 2 });
        await fetchSystemDetails();
      } else {
        // backend‑returned error (e.g. lock busy)
        message.error({ content: data.message, key, duration: 4 });
      }
    } catch (e: any) {
      message.error({
        content: `Failed to ${action} the system. ${e.message || ''}`,
        key,
        duration: 4,
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Fetch logs for a container
  const fetchContainerLogs = async (name: string) => {
    setLoadingLogs(true);
    setErrorLogs(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/systems/${systemId}/containers/${encodeURIComponent(
          name
        )}/logs?limit=200`,
        { credentials: 'include', method: 'GET' }
      );
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const json = await res.json();
      setLogs(json.logs || []);
    } catch (e: any) {
      setErrorLogs(e.message);
      setLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleContainerClick = (name: string) => {
    setSelectedContainer(name);
    setIsModalOpen(true);
    fetchContainerLogs(name);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedContainer(null);
    setLogs([]);
    setErrorLogs(null);
  };

  const handleRefreshLogs = () => {
    if (selectedContainer) {
      fetchContainerLogs(selectedContainer);
    }
  };

  // --- render logic ---
  if (!canRead(PERMISSIONS.SYSTEM_MANAGEMENT)) {
    return <Tag>You have no access to this section.</Tag>;
  }

  if (loading) {
    return <Loading message="Loading System Details..." />;
  }

  if (error) {
    const isTimeout = error.status === 504;
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            Back
          </Button>
          <div className="flex items-center justify-center py-12">
            <div className="text-center max-w-md">
              <div className="mb-4">
                <ExclamationCircleOutlined
                  className={`text-6xl ${
                    isTimeout ? 'text-orange-500' : 'text-red-500'
                  }`}
                />
              </div>
              <Title level={2}>
                {isTimeout ? 'Connection Timed Out' : 'Failed to Load System'}
              </Title>
              <Paragraph type="secondary" className="mb-6">
                {error.message}
              </Paragraph>
              <div className="flex gap-2 justify-center">
                <Button
                  type="primary"
                  icon={<SyncOutlined />}
                  onClick={fetchSystemDetails}
                >
                  Retry
                </Button>
                <Button onClick={() => navigate(-1)}>Go Back</Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!system) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <Result
          status="404"
          title="404"
          subTitle="Sorry, the system you're looking for does not exist."
          extra={
            <Button onClick={() => navigate(-1)} type="primary">
              Go Back
            </Button>
          }
        />
      </div>
    );
  }

  const isHealthy =
    system.health.includes('100') || system.health.includes('9');

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <Row align="middle" justify="space-between">
          <Col>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              className="mb-4"
            >
              Back
            </Button>
            <Title level={2}>{system.name}</Title>
            <Paragraph type="secondary">{system.description}</Paragraph>
            <Tag color={system.status === 'Active' ? 'green' : 'red'}>
              {system.status}
            </Tag>
          </Col>
          {canWrite(PERMISSIONS.SYSTEM_MANAGEMENT) && (
            <Col>
              <div className="flex gap-2">
                <Popconfirm
                  title="Are you sure you want to start the stack?"
                  onConfirm={() => handleControlClick('start')}
                  okText="Yes"
                  cancelText="No"
                  disabled={!!actionLoading}
                >
                  <Button
                    icon={<PlayCircleOutlined />}
                    loading={actionLoading === 'start'}
                    disabled={!!actionLoading}
                  >
                    Start
                  </Button>
                </Popconfirm>

                <Popconfirm
                  title="Are you sure you want to stop the stack?"
                  onConfirm={() => handleControlClick('stop')}
                  okText="Yes"
                  cancelText="No"
                  disabled={!!actionLoading}
                >
                  <Button
                    icon={<PauseCircleOutlined />}
                    loading={actionLoading === 'stop'}
                    disabled={!!actionLoading}
                  >
                    Stop
                  </Button>
                </Popconfirm>

                <Popconfirm
                  title="Are you sure you want to restart the stack?"
                  onConfirm={() => handleControlClick('restart')}
                  okText="Yes"
                  cancelText="No"
                  disabled={!!actionLoading}
                >
                  <Button
                    type="primary"
                    icon={<RedoOutlined />}
                    loading={actionLoading === 'restart'}
                    disabled={!!actionLoading}
                  >
                    Restart
                  </Button>
                </Popconfirm>
              </div>
            </Col>
          )}
        </Row>
      </div>

      {/* Containers list */}
      <div className="mt-6">
        <Card title="Docker Containers">
          <List
            itemLayout="horizontal"
            dataSource={system.containers}
            renderItem={(container: Container) => (
              <List.Item
                style={{
                  cursor: 'pointer',
                  padding: '16px',
                  borderRadius: '8px',
                  margin: '8px 0',
                  border: '1px solid #f0f0f0',
                  backgroundColor: '#fafafa',
                  transition: 'all 0.3s ease',
                }}
                className="hover:bg-blue-50 hover:border-blue-300 hover:shadow-md"
                onClick={() => handleContainerClick(container.name)}
              >
                <List.Item.Meta
                  avatar={
                    <div
                      style={{
                        backgroundColor: container.status.includes('Up')
                          ? '#52c41a'
                          : '#faad14',
                        borderRadius: '50%',
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                      }}
                    >
                      <FileTextOutlined style={{ fontSize: '18px' }} />
                    </div>
                  }
                  title={
                    <div className="flex items-center justify-between">
                      <Text
                        strong
                        style={{ fontSize: '16px', color: '#1890ff' }}
                      >
                        {container.name}
                      </Text>
                      <Tag
                        color={
                          container.status.includes('Up') ? 'success' : 'warning'
                        }
                        style={{ fontWeight: 'bold' }}
                      >
                        {container.status}
                      </Tag>
                    </div>
                  }
                  description={
                    <div className="flex items-center gap-2 mt-2">
                      <Text type="secondary">Click to view container logs</Text>
                      <span style={{ color: '#1890ff' }}>→</span>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      </div>

      {/* Logs Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <FileTextOutlined />
            <span>Container Logs: {selectedContainer}</span>
          </div>
        }
        open={isModalOpen}
        onCancel={handleModalClose}
        width={1000}
        footer={[
          <Button key="refresh" icon={<SyncOutlined />} onClick={handleRefreshLogs}>
            Refresh
          </Button>,
          <Button key="close" type="primary" onClick={handleModalClose}>
            Close
          </Button>,
        ]}
      >
        {loadingLogs ? (
          <div className="flex justify-center py-8">
            <Spin size="large" tip="Loading logs…" />
          </div>
        ) : errorLogs ? (
          <Alert
            message="Failed to load logs"
            description={errorLogs}
            type="error"
            showIcon
            action={
              <Button size="small" onClick={handleRefreshLogs}>
                Retry
              </Button>
            }
          />
        ) : logs.length === 0 ? (
          <div className="text-center py-8">
            <Text type="secondary">No logs available for this container.</Text>
          </div>
        ) : (
          <div
            style={{
              maxHeight: '500px',
              overflowY: 'auto',
              overflowX: 'auto',
              backgroundColor: '#001529',
              color: '#fff',
              padding: '16px',
              borderRadius: '6px',
              fontFamily: 'monospace',
              fontSize: '13px',
              lineHeight: '1.4',
            }}
          >
            {logs.map((entry, idx) => (
              <div
                key={idx}
                style={{
                  marginBottom: '2px',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'flex-start',
                }}
              >
                <Text
                  type="secondary"
                  style={{
                    color: '#8c8c8c',
                    marginRight: '12px',
                    fontSize: '12px',
                    flexShrink: 0,
                    minWidth: 'fit-content',
                  }}
                >
                  {new Date(Number(entry.ts) / 1e6).toLocaleString()}
                </Text>
                <span style={{ color: '#fff' }}>{entry.line}</span>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SystemViewPage;
